import styles from "./imgUpload.module.scss";
import { useContext, createContext, useCallback, useEffect, useState, useMemo } from "react";
import Icon from "../../icons/Icon";
import { ProfilepicCamera } from "../../icons/24";
import { Image, Camera } from "../../icons/40";
import React, { useRef, useImperativeHandle } from "react";
import { ButtonBox } from "../../button/Button";
import imageCompression from "browser-image-compression";
import { SkeletonBase } from "../../addons/skeleton/Skeleton";

/**
 * png로 변환하려고 하는 경우 압축 과정에서 매우 오래걸리므로 다른 확장자를 사용하는 것을 권장합니다.
 */

// 이미지 업로드 타입
interface ImgUploadProps {
  type?: "button" | "profile" | "list";
  replace?: boolean;
  image: string[];
  setImage: React.Dispatch<string[]>;
  iconSrc?: string;
  uniqueKey: string;
  children?: React.ReactNode;
  file: File[];
  setFile: React.Dispatch<File[]>;
  blob?: Blob[];
  setBlob?: React.Dispatch<Blob[]>;
  maxLength?: number;
}

// 컨텍스트 타입
export type ImgUploadContextProps = Omit<ImgUploadProps, "children"> & {
  inCompressed?: boolean;
  setInCompressed?: React.Dispatch<boolean>;
};

// 컨텍스트 기본값 설정
const defaultContext: ImgUploadContextProps = {
  type: "button",
  replace: false,
  image: [],
  setImage: () => {},
  iconSrc: "",
  uniqueKey: "",
  file: [],
  setFile: () => {},
  blob: [],
  setBlob: () => {},
  inCompressed: false,
  setInCompressed: () => {},
};

// 이미지 업로드 컨텍스트
const imgUploadPreviewContext = createContext<ImgUploadContextProps>(defaultContext);

// 파일 디테일 확인 핸들러 (파일 타입, 사이즈 확인)
export const fileDetailCheckHandler = async ({
  file,
  maxSizeMB,
  showAlert = false,
}: {
  file: File;
  maxSizeMB?: number;
  showAlert?: boolean;
}): Promise<boolean> => {
  const fileTypes = ["jpg", "jpeg", "png", "webp", "gif"];
  const size = file.size;
  const extension = file.name.split(".").pop()?.toLowerCase();
  const isSuccess = fileTypes.includes(extension!);

  // 파일 크기 초과 처리
  if (maxSizeMB && size > maxSizeMB * 1024 * 1024) {
    if (showAlert) {
      const maxSizeMBMB = maxSizeMB + "MB";
      alert(`파일 크기는 ${maxSizeMBMB} 이하로 업로드 가능합니다.`);
    }
    return false;
  }

  // 확장자 확인 처리
  if (isSuccess) {
    return true;
  } else {
    if (showAlert) {
      alert(`${fileTypes.join(",")} 파일만 업로드 가능합니다`);
    }
    return false;
  }
};

// 이미지 업로드 프로바이더
const ImgProvider = ({
  type = "button",
  replace = false,
  image,
  setImage,
  iconSrc,
  uniqueKey,
  children,
  file,
  setFile,
  blob,
  setBlob,
  maxLength,
}: ImgUploadProps) => {
  const [inCompressed, setInCompressed] = useState(false);

  return (
    <imgUploadPreviewContext.Provider
      value={{
        type,
        replace,
        image,
        setImage,
        iconSrc: iconSrc || "",
        uniqueKey,
        file,
        setFile,
        blob,
        setBlob,
        inCompressed,
        setInCompressed,
        maxLength: maxLength || 99,
      }}
    >
      {type === "profile" ? (
        <div
          className={`${styles["img-upload-profile-wrap"]} ${image.length > 0 ? styles["has-image"] : styles["no-image"]}`}
        >
          <div className={styles["img-upload-profile"]}>{children}</div>
          <Icon IconComponent={<ProfilepicCamera />} size={24} />
        </div>
      ) : type === "button" ? (
        <>{children}</>
      ) : (
        <div
          className={`${styles["img-upload-list-wrap"]} ${image.length > 0 ? styles["has-image"] : styles["no-image"]}`}
        >
          <div className={styles["img-upload-list"]}>{children}</div>
        </div>
      )}
    </imgUploadPreviewContext.Provider>
  );
};

/**
 * 이미지 업로드 컴포넌트
 *
 * @returns 이미지 업로드 컴포넌트
 * @param maxLength 최대 업로드 가능 이미지 수
 * @param onClick 클릭 이벤트 핸들러
 * @param disabled 비활성화 여부
 */
interface IImgUploadProps {
  onClick?: (valid: boolean) => void;
  disabled?: boolean;
  hidden?: boolean;
  onUploadComplete?: ({ url, blob }: { url: string | null; blob: Blob | null }) => void;
  maxSizeMB?: number;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  mimeType?: string;
  enablePreview?: boolean;
  maxLength?: number;
}

const ImgUpload = React.forwardRef<HTMLLabelElement, IImgUploadProps>(
  (
    {
      onClick,
      disabled = false,
      hidden = false,
      onUploadComplete, // 이미지 업로드 완료 후 콜백, 리사이징 처리 시 url, blob 반환 (아닐 시 null)
      maxWidth = 1920,
      maxHeight = 1920,
      quality,
      maxSizeMB = 20,
      onChange,
      mimeType = "image/jpeg",
      enablePreview = true,
      maxLength: uploadMaxLength,
    },
    ref,
  ) => {
    // label ref
    const labelRef = useRef<HTMLLabelElement | null>(null);
    // 전달된 ref가 있을 경우 labelRef 연결
    useImperativeHandle(ref, () => {
      if (labelRef.current === null) {
        throw new Error("labelRef is not assigned yet");
      }
      return labelRef.current;
    });

    // 이미지 업로드 컨텍스트
    const {
      type,
      image,
      setImage,
      iconSrc,
      file,
      setFile,
      uniqueKey,
      replace,
      blob,
      setBlob,
      setInCompressed,
      maxLength: contextMaxLength,
    } = useContext(imgUploadPreviewContext)!;

    const maxLength = uploadMaxLength || contextMaxLength || 99;

    // 파일 처리중 여부
    const inEncodingProcess = useRef<boolean>(false);
    const dataRef = useRef({
      image,
      file,
      blob,
    });
    useEffect(() => {
      dataRef.current = {
        image,
        file,
        blob,
      };
    }, [image, file, blob]);

    // input file 이벤트 핸들러 (이미지 업로드)
    const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const newFile = e.target.files[0];

        const valid = await fileDetailCheckHandler({
          file: newFile,
          maxSizeMB,
          showAlert: true,
        });

        const { file, image, blob } = dataRef.current;

        if (valid) {
          // 파일 업데이트
          if (!replace) {
            setFile([...file, newFile]);
          } else {
            if (file.length < maxLength) {
              setFile([...file, newFile]);
            } else {
              const newFileList = file.slice(0, file.length - 1);
              setFile([...newFileList, newFile]);
            }
          }
          onChange && onChange(e);

          if (!enablePreview) {
            onUploadComplete &&
              onUploadComplete({
                url: null,
                blob: null,
              });
          } else {
            // 인코딩 프로세스 중이면 이벤트 중지
            if (inEncodingProcess.current) return;
            // 인코딩 프로세스 플래그 설정
            inEncodingProcess.current = true;
            setInCompressed && setInCompressed(true);
            // 파일 리사이징
            try {
              const options = {
                maxSizeMBMB: 0.8,
                maxWidthOrHeight: Math.max(maxHeight, maxWidth),
                useWebWorker: true,
                initialQuality: quality,
                fileType: mimeType,
              };
              const compressedFileBlob = await imageCompression(newFile, options);
              const compressedImageUrl = URL.createObjectURL(compressedFileBlob);

              // 미리보기 이미지 업데이트
              if (!replace) {
                if (compressedImageUrl) {
                  setImage([...image, compressedImageUrl]);
                }
                if (compressedFileBlob && blob && setBlob) {
                  setBlob([...blob, compressedFileBlob]);
                }
              } else {
                if (compressedImageUrl) {
                  if (image.length < maxLength) {
                    setImage([...image, compressedImageUrl]);
                  } else {
                    const newImageList = image.slice(0, image.length - 1);
                    setImage([...newImageList, compressedImageUrl]);
                  }
                }
                if (compressedFileBlob && blob && setBlob) {
                  if (blob.length < maxLength) {
                    setBlob([...blob, compressedFileBlob]);
                  } else {
                    const newBlobList = blob.slice(0, blob.length - 1);
                    setBlob([...newBlobList, compressedFileBlob]);
                  }
                }
              }
              onUploadComplete &&
                onUploadComplete({
                  url: compressedImageUrl,
                  blob: compressedFileBlob,
                });
            } catch (error) {
              alert(`이미지 업로드에 실패했습니다.`);
            } finally {
              // console.log("encoding process done");
            }
            setInCompressed && setInCompressed(false);
            inEncodingProcess.current = false;
          }
        }
        e.target.value = ""; // 파일 업로드 후 input 초기화
      }
    }, []);

    // label click
    const handleLabelClick = useCallback(
      (e: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
        // 비활성화 상태거나 인코딩 중이면 이벤트 중지
        if (disabled || inEncodingProcess.current) {
          e.preventDefault();
          return;
        }

        const { image } = dataRef.current;

        onClick && onClick(!maxLength || image.length < maxLength);
        if (type !== "profile" && !replace) {
          if (maxLength && image.length >= maxLength) {
            e.preventDefault();
            alert(`이미지는 최대 ${maxLength}개까지 업로드 가능합니다.`);
            return;
          }
        }
      },
      [disabled],
    );

    return (
      <div
        className={`${styles["img-upload-button-wrap"]} ${type ? styles[type] : ""} ${hidden ? "only-sr" : ""}`}
      >
        <input
          className={styles["input-upload"]}
          id={`input-upload-${uniqueKey}`}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
        />
        <label
          className={styles["label-upload"]}
          htmlFor={`input-upload-${uniqueKey}`}
          ref={labelRef}
          onClick={(e) => {
            handleLabelClick(e);
          }}
        >
          {iconSrc ? (
            <img
              src={iconSrc}
              alt="이미지 업로드"
              onError={(e) => {
                e.currentTarget.style.opacity = "0";
              }}
              onLoad={(e) => {
                e.currentTarget.style.opacity = "1";
              }}
            />
          ) : type === "button" ? (
            <Icon IconComponent={<Image />} size={40} />
          ) : (
            <Icon IconComponent={<Camera />} size={40} />
          )}
          {type === "list" && (
            <span>
              {image.length} / {maxLength}
            </span>
          )}
        </label>
      </div>
    );
  },
);
ImgUpload.displayName = "ImgUpload";

/**
 * 이미지 미리보기 컴포넌트
 *
 * @returns 이미지 미리보기 컴포넌트
 */
interface IImgPreviewProps {
  useCustomStyle?: boolean;
  onImgsLoadChange?: (status: boolean) => void;
}

const ImgPreview = React.forwardRef<HTMLDivElement, IImgPreviewProps>(
  ({ useCustomStyle = false }, ref) => {
    const {
      type,
      image,
      setImage,
      file,
      setFile,
      blob,
      setBlob,
      inCompressed,
      replace,
      maxLength = 99,
    } = useContext(imgUploadPreviewContext)!;

    const dataRef = useRef({
      image,
      file,
      blob,
    });
    useEffect(() => {
      dataRef.current = {
        image,
        file,
        blob,
      };
    }, [image, file, blob]);

    // button ref
    const buttonRef = useRef<HTMLDivElement | null>(null);
    // 전달된 ref가 있을 경우 buttonRef 연결
    useImperativeHandle(ref, () => {
      if (buttonRef.current === null) {
        throw new Error("labelRef is not assigned yet");
      }
      return buttonRef.current;
    });

    const imgLoaderType = useMemo(() => {
      if (inCompressed) {
        if (replace && image.length === maxLength) {
          return "replace";
        }
        if (!replace || (replace && image.length < maxLength)) {
          return "next";
        }
      }
      return "";
    }, [inCompressed, maxLength, image?.length]);

    const deleteHandler = useCallback((idx: number) => {
      const { image, file, blob } = dataRef.current;

      const newImages = image.filter((_, i) => i !== idx);
      setImage(newImages);
      const newFiles = file.filter((_, i) => i !== idx);
      setFile(newFiles);
      if (blob && blob.length > 0 && setBlob) {
        const newBlobs = blob.filter((_, i) => i !== idx);
        setBlob(newBlobs);
      }
    }, []);

    return (
      <div
        className={`${styles["img-upload-preview-wrap"]} ${type && !useCustomStyle ? styles[type] : "img-upload-preview-custom-style"}`}
      >
        <ul>
          {image &&
            image.map((img, idx) => (
              <li
                key={idx}
                className={`${imgLoaderType === "replace" && idx === maxLength - 1 ? styles["show-loader"] : ""}`}
              >
                <>
                  <div className={`${styles["img-preview"]}`}>
                    {imgLoaderType === "replace" && idx === maxLength - 1 ? (
                      <SkeletonBase width="100%" height="100%" />
                    ) : null}
                    <img
                      key={img}
                      className={`${styles["img-preview-img"]}`}
                      src={img}
                      alt={`이미지 업로드 ${idx}`}
                      style={{
                        opacity: imgLoaderType === "replace" && idx === maxLength - 1 ? 0 : 1,
                      }}
                    />
                  </div>
                  <ButtonBox
                    key={"delete-" + idx}
                    ref={buttonRef}
                    className={`${styles["img-preview-delete"]} ${type === "profile" || useCustomStyle ? "only-sr" : ""}`}
                    type="icon"
                    onClick={() => deleteHandler(idx)}
                  >
                    <IconCircleG700Memo type={type ? type : "list"} />
                  </ButtonBox>
                </>
              </li>
            ))}
          {imgLoaderType === "next" && (
            <li className={styles["show-loader"]}>
              <SkeletonBase width="100%" height="100%" />
            </li>
          )}
        </ul>
      </div>
    );
  },
);
ImgPreview.displayName = "ImgPreview";

const IconCircleG700Memo = React.memo(({ type }: { type: "button" | "profile" | "list" }) => (
  <Icon
    name="XcircleG700"
    size={18}
    styleTag={type === "button" ? `circle{fill: #191919;} path{stroke:#ffffff;}` : ""}
  />
));
IconCircleG700Memo.displayName = "IconCircleG700Memo";

export { ImgProvider, ImgUpload, ImgPreview, imgUploadPreviewContext };
